import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, KeyRound, Copy, Plus, Trash2, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatDate } from "@/lib/statusColor";

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resetInfo, setResetInfo] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState({ email: "", name: "", password: "", role: "sub_admin" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const resetPw = async (u) => {
    if (!window.confirm(`Reset password for ${u.email}? A new temporary password will be generated.`)) return;
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`, {});
      setResetInfo(data);
      toast.success("Password reset");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteUser = async (u) => {
    if (u.role === "master_admin") return toast.error("Master admin accounts are protected");
    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/users/${u.id}`);
      setUsers((p) => p.filter((x) => x.id !== u.id));
      toast.success(`Deleted ${u.email}`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const submitNew = async () => {
    const { email, name, password, role } = newUser;
    if (!email || !name || !password) return toast.error("Fill all fields");
    if (password.length < 6) return toast.error("Password must be ≥ 6 characters");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/users", { email: email.toLowerCase(), name, password, role });
      setUsers((p) => [...p, { ...data, timezone: "UTC", digest_opt_out: false, created_at: new Date().toISOString() }]);
      setNewUser({ email: "", name: "", password: "", role: "sub_admin" });
      setCreating(false);
      toast.success(`Created ${data.email}`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const copyPw = async () => {
    if (!resetInfo) return;
    await navigator.clipboard.writeText(resetInfo.temporary_password);
    toast.success("Copied to clipboard");
  };

  if (user && user.role !== "master_admin" && user.role !== "sub_admin") {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <main className="max-w-3xl mx-auto px-6 py-10">
          <Card className="p-8 rounded-md border-slate-200 shadow-sm text-center">
            <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
              Access denied
            </h1>
            <p className="text-slate-500 mt-2">Only an admin or sub-admin can manage users.</p>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 py-8">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4">
          <ArrowLeft size={14} /> Back to dashboard
        </Link>
        <div className="flex items-baseline justify-between mb-6 flex-wrap gap-2">
          <div>
            <h1 className="text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
              Manage users
            </h1>
            <p className="text-slate-500 mt-2">Create, reset or delete admin accounts. The master admin account is protected and cannot be deleted.</p>
          </div>
          <Button onClick={() => setCreating((v) => !v)} data-testid="new-user-btn">
            {creating ? <><X size={14} className="mr-1.5" /> Cancel</> : <><Plus size={14} className="mr-1.5" /> New user</>}
          </Button>
        </div>

        {creating && (
          <Card className="p-4 rounded-md border-slate-200 shadow-sm mb-6" data-testid="new-user-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label>Name</Label><Input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} data-testid="new-user-name" /></div>
              <div><Label>Email</Label><Input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} data-testid="new-user-email" /></div>
              <div><Label>Password <span className="text-xs text-slate-400">(≥ 6 chars)</span></Label><Input type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} data-testid="new-user-password" /></div>
              <div><Label>Role</Label>
                <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                  <SelectTrigger className="h-10" data-testid="new-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sub_admin">Sub-Admin</SelectItem>
                    <SelectItem value="master_admin">Master Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end mt-3">
              <Button onClick={submitNew} disabled={busy} data-testid="new-user-submit">{busy ? "Creating…" : "Create user"}</Button>
            </div>
          </Card>
        )}

        {resetInfo && (
          <Card className="p-4 rounded-md border-emerald-200 bg-emerald-50 mb-6" data-testid="reset-info-card">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-xs uppercase tracking-wider text-emerald-800 font-semibold">Password reset for</div>
                <div className="text-slate-900 font-semibold">{resetInfo.email}</div>
                <div className="mt-2 flex items-center gap-2">
                  <code className="px-2 py-1 rounded bg-white border border-emerald-300 text-slate-900 font-mono text-sm" data-testid="reset-info-pw">{resetInfo.temporary_password}</code>
                  <Button size="sm" variant="outline" onClick={copyPw} data-testid="reset-info-copy"><Copy size={13} className="mr-1.5" /> Copy</Button>
                </div>
              </div>
              <button type="button" onClick={() => setResetInfo(null)} className="text-emerald-800 text-xs hover:underline">Dismiss</button>
            </div>
          </Card>
        )}

        <Card className="rounded-md border-slate-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-8 text-slate-500 text-sm text-center">Loading…</div>
          ) : (
            <table className="w-full text-sm" data-testid="users-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3 font-semibold">Name</th>
                  <th className="px-4 py-3 font-semibold">Email</th>
                  <th className="px-4 py-3 font-semibold">Role</th>
                  <th className="px-4 py-3 font-semibold">Created</th>
                  <th className="px-4 py-3 font-semibold w-32"></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-slate-100" data-testid={`user-row-${u.email}`}>
                    <td className="px-4 py-3 font-medium text-slate-900">{u.name}</td>
                    <td className="px-4 py-3 text-slate-600">{u.email}</td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className={u.role === "master_admin" ? "border-fuchsia-300 bg-fuchsia-50 text-fuchsia-800" : u.role === "sub_admin" ? "border-indigo-300 bg-indigo-50 text-indigo-800" : "border-slate-300"}>
                        {u.role || "user"}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">{u.created_at ? formatDate(u.created_at) : ""}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => resetPw(u)} data-testid={`reset-pw-${u.email}`}>
                          <KeyRound size={13} className="mr-1.5" /> Reset
                        </Button>
                        <Button
                          size="sm" variant="outline"
                          onClick={() => deleteUser(u)}
                          disabled={u.role === "master_admin" || u.id === user?.id || busy}
                          title={u.role === "master_admin" ? "Master admins are protected" : (u.id === user?.id ? "You cannot delete yourself" : "Delete")}
                          className="text-rose-700 hover:text-rose-800 border-rose-200 hover:bg-rose-50 disabled:opacity-40"
                          data-testid={`delete-user-${u.email}`}
                        >
                          <Trash2 size={13} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </main>
    </div>
  );
}
